import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { LmsApiService, LessonResponse } from "../backend";
import { useAuth } from "@/features/auth/context/AuthContext";
import BlockRenderer from "../components/BlockRenderer";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Loading } from "@/components/ui/loading";

const lmsService = new LmsApiService();
const COURSE_ID = 1;

export default function LessonView() {
  const { lessonId } = useParams();
  const { profile } = useAuth();
  const navigate = useNavigate();

  const [lessonData, setLessonData] = useState<LessonResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLesson = async () => {
      if (!profile?.id || !lessonId) return;

      setLoading(true);
      try {
        const data = await lmsService.getLessonContent(
          profile.id,
          Number(lessonId)
        );
        setLessonData(data);
      } catch (error) {
        console.error("Failed to load lesson:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchLesson();
  }, [lessonId, profile?.id]);

  if (loading)
    return (
      <div className="py-20">
        <Loading />
      </div>
    );
  if (!lessonData) return <div>Lesson not found.</div>;

  // Simple navigation logic (mocked for now, ideally we get prev/next IDs from backend or context)
  const currentId = Number(lessonId);
  const nextId = currentId < 204 ? currentId + 1 : null; // Mock logic based on mock data IDs
  const prevId = currentId > 201 ? currentId - 1 : null;

  const handleNext = async () => {
    if (!profile?.id || !lessonId) return;

    try {
      await lmsService.completeLesson(profile.id, COURSE_ID, Number(lessonId));
      if (nextId) {
        navigate(`/job-recognition-course/lesson/${nextId}`);
      } else {
        navigate("/job-recognition-course");
      }
    } catch (error) {
      console.error("Failed to complete lesson:", error);
    }
  };

  return (
    <div className="max-w-3xl mx-auto pb-20 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          {lessonData.lesson_title}
        </h1>
      </div>

      <div className="space-y-8">
        {lessonData.blocks.map((block) => (
          <BlockRenderer key={block.id} block={block} courseId={COURSE_ID} />
        ))}
      </div>

      <div className="mt-16 pt-8 border-t flex justify-between">
        <Button
          variant="outline"
          onClick={() =>
            prevId && navigate(`/job-recognition-course/lesson/${prevId}`)
          }
          disabled={!prevId}
          className="flex items-center gap-2"
        >
          <ChevronLeft className="w-4 h-4" /> Previous Lesson
        </Button>

        <Button
          onClick={handleNext}
          className="bg-[#471999] hover:bg-[#38147a] flex items-center gap-2"
        >
          {nextId ? (
            <>
              Next Lesson <ChevronRight className="w-4 h-4" />
            </>
          ) : (
            <>
              Finish Course <ChevronRight className="w-4 h-4" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
